import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { calendarYearPath } from "@/lib/calendar/dates";
import { PdfExportButton } from "@/components/PdfExportButton";
import { LongWeekendTable, FaqJsonLd, CrossLinks } from "@/components/pages/PseoExtras";
import { getHolidaysForYear, isEstimatedYear, DISCLAIMER_SKB } from "@/data/holidays";
import { HolidayTable, YearLinks } from "@/components/pages/LiburNasionalPage";
import { useLocale } from "@/i18n/LocaleContext";
import type { Locale } from "@/i18n/types";
import { FeaturedSvg } from "@/components/seo/FeaturedSvg";

export function CutiBersamaPage({ y, locale }: { y: number; locale?: Locale }) {
  return (
    <SiteShell locale={locale}>
      <Body y={y} />
    </SiteShell>
  );
}

function Body({ y }: { y: number }) {
  const { t, href, locale } = useLocale();
  const list = getHolidaysForYear(y).filter((h) => h.type === "cuti-bersama");
  const allHolidays = getHolidaysForYear(y);
  const faq =
    locale === "id"
      ? [
          { q: `Berapa hari cuti bersama ${y}?`, a: `Pemerintah menetapkan ${list.length} tanggal cuti bersama untuk tahun ${y} melalui SKB 3 Menteri.` },
          { q: `Apakah cuti bersama mengurangi jatah cuti tahunan?`, a: `Ya. Cuti bersama termasuk pengurang hak cuti tahunan pegawai sesuai aturan SKB 3 Menteri.` },
          { q: `Kapan cuti bersama Idul Fitri ${y}?`, a: `Tanggal cuti bersama Idul Fitri ${y} tercantum di tabel di atas, mengikuti penetapan Hari Raya Idul Fitri.` },
          { q: `Apakah pegawai swasta wajib mengikuti cuti bersama?`, a: `Penerapan cuti bersama untuk swasta bersifat menyesuaikan kebijakan masing-masing perusahaan, dengan acuan SKB 3 Menteri.` },
        ]
      : [
          { q: `How many joint leave days in ${y}?`, a: `The government has set ${list.length} joint leave dates for ${y} through the joint ministerial decree.` },
          { q: `Does joint leave reduce annual leave allowance?`, a: `Yes. Joint leave reduces the employee's annual leave entitlement under the joint ministerial decree.` },
          { q: `When is joint leave for Eid al-Fitr ${y}?`, a: `The Eid al-Fitr ${y} joint leave dates are in the table above, following the Eid date.` },
          { q: `Are private employees required to follow joint leave?`, a: `For private companies, joint leave is implemented based on each company's policy, using the joint ministerial decree as a reference.` },
        ];

  return (
    <>
      <FaqJsonLd items={faq} />
      <nav className="py-4 text-sm text-muted-foreground">
        <Link to={href("/") as never} className="hover:text-primary">{t.common.home}</Link> /{" "}
        <Link to={href(calendarYearPath(y)) as never} className="hover:text-primary">{y}</Link> / {t.common.jointLeave}
      </nav>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.common.jointLeave} {y}</h1>
          <p className="mt-1 text-muted-foreground">
            {t.common.cutiCount(list.length)}
            {isEstimatedYear(y) && (
              <span className="ml-2 rounded-md bg-muted px-2 py-0.5 text-xs">{t.common.estimatedData}</span>
            )}
          </p>
        </div>
        <FeaturedSvg
          variant="joint-leave"
          title={`${t.common.jointLeave} ${y}`}
          subtitle={t.common.cutiCount(list.length)}
          badge={String(y)}
          locale={locale}
          className="hidden md:block md:w-72"
        />
        <PdfExportButton mode="year" year={y} />
      </header>

      <article className="prose prose-sm mb-6 max-w-none text-muted-foreground">
        <p>
          {locale === "id" ? (
            <><strong>Cuti bersama {y}</strong> ditetapkan oleh SKB 3 Menteri dan biasanya melekat pada hari raya besar seperti Idul Fitri, Natal, dan tahun baru. Halaman ini memuat daftar lengkap tanggalnya beserta peluang long weekend untuk perencanaan liburan.</>
          ) : (
            <><strong>Joint leave {y}</strong> is set by the joint ministerial decree and usually attached to major holidays such as Eid al-Fitr, Christmas, and New Year. This page lists the dates and the long weekends you can plan around.</>
          )}
        </p>
      </article>

      <HolidayTable items={list} year={y} />

      <div className="mt-6">
        <LongWeekendTable year={y} holidays={allHolidays} />
      </div>

      <p className="mt-6 rounded-md bg-muted p-3 text-xs text-muted-foreground">{DISCLAIMER_SKB}</p>
      <YearLinks current={y} prefix="cuti-bersama" />
      <CrossLinks year={y} current="cuti" />

      <section className="mt-8">
        <h2 className="mb-3 text-lg font-semibold">{t.faq.title(`${t.common.jointLeave} ${y}`)}</h2>
        <div className="space-y-3">
          {faq.map((f) => (
            <div key={f.q} className="rounded-md border border-border bg-card p-3">
              <p className="text-sm font-semibold">{f.q}</p>
              <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}